import React, { forwardRef, useState } from 'react';
import { Pressable, Switch as RNSwitch, TextInput, View, FlatList, type TextInputInstance, type TextInputProps, type ViewStyle } from 'react-native';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { Text } from './Text';
import { radius, useTheme } from './theme';

/** Label + control + hint/error text. */
export function Field({ label, required, error, hint, children, style }: { label?: string; required?: boolean; error?: string; hint?: string; children: React.ReactNode; style?: ViewStyle }) {
  const { colors } = useTheme();
  return (
    <View style={[{ gap: 6 }, style]}>
      {!!label && (
        <Text variant="label">
          {label}
          {required ? <Text color={colors.danger}> *</Text> : null}
        </Text>
      )}
      {children}
      {error ? (
        <Text size={12} color={colors.danger}>
          {error}
        </Text>
      ) : hint ? (
        <Text variant="small">{hint}</Text>
      ) : null}
    </View>
  );
}

export const useInputStyle = (invalid?: boolean, disabled?: boolean, multiline?: boolean) => {
  const { colors } = useTheme();
  return {
    minHeight: multiline ? 84 : 40,
    borderWidth: 1,
    borderColor: invalid ? colors.danger : colors.borderStrong,
    borderRadius: radius.md,
    paddingHorizontal: 12,
    paddingVertical: multiline ? 10 : 8,
    backgroundColor: disabled ? colors.surface2 : colors.surface,
    color: colors.text,
    fontSize: 14,
    textAlignVertical: multiline ? ('top' as const) : ('center' as const),
  };
};

export interface TextFieldProps extends Omit<TextInputProps, 'onChange'> {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  disabled?: boolean;
  containerStyle?: ViewStyle;
}

/** Text input with optional label/error. Use keyboardType / secureTextEntry / multiline as usual. */
export const TextField = forwardRef<TextInputInstance, TextFieldProps>(function TextFieldImpl({ label, required, error, hint, disabled, containerStyle, style, multiline, ...rest }, ref) {
  const { colors } = useTheme();
  const base = useInputStyle(!!error, disabled, multiline);
  const input = (
    <TextInput
      ref={ref}
      placeholderTextColor={colors.faint}
      editable={!disabled}
      multiline={multiline}
      accessibilityLabel={rest.accessibilityLabel ?? label}
      {...rest}
      style={[base, style]}
    />
  );
  if (!label && !error && !hint) return <View style={containerStyle}>{input}</View>;
  return (
    <Field label={label} required={required} error={error} hint={hint} style={containerStyle}>
      {input}
    </Field>
  );
});

export interface Option<V extends string | number = string> {
  value: V;
  label: string;
  description?: string;
}

/** Select rendered as a pressable field that opens a searchable modal list. */
export function Select<V extends string | number>({
  label,
  value,
  options,
  onChange,
  placeholder = 'Select…',
  required,
  error,
  hint,
  disabled,
  searchable,
  containerStyle,
  testID,
}: {
  label?: string;
  value: V | null | undefined;
  options: Option<V>[];
  onChange: (v: V) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  disabled?: boolean;
  searchable?: boolean;
  containerStyle?: ViewStyle;
  testID?: string;
}) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const base = useInputStyle(!!error, disabled);
  const current = options.find((o) => o.value === value);
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  const control = (
    <>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={label ? `${label}: ${current?.label ?? placeholder}` : current?.label ?? placeholder}
        disabled={disabled}
        onPress={() => setOpen(true)}
        style={[base, { flexDirection: 'row', alignItems: 'center', gap: 8 }]}
      >
        <Text numberOfLines={1} style={{ flex: 1 }} color={current ? colors.text : colors.faint}>
          {current?.label ?? placeholder}
        </Text>
        <Icon name="chevronDown" size={16} color={colors.muted} />
      </Pressable>
      <Modal open={open} onClose={() => setOpen(false)} title={label ?? placeholder} width={440} noScroll>
        {(searchable ?? options.length > 8) && (
          <View style={{ padding: 12, paddingBottom: 4 }}>
            <TextField placeholder="Search…" value={q} onChangeText={setQ} autoFocus accessibilityLabel="Search options" />
          </View>
        )}
        <FlatList
          data={shown}
          keyExtractor={(o) => String(o.value)}
          style={{ flexGrow: 0 }}
          contentContainerStyle={{ padding: 8 }}
          keyboardShouldPersistTaps="handled"
          renderItem={({ item }) => {
            const sel = item.value === value;
            return (
              <Pressable
                accessibilityRole="button"
                accessibilityState={{ selected: sel }}
                onPress={() => {
                  onChange(item.value);
                  setOpen(false);
                  setQ('');
                }}
                style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 12, paddingVertical: 11, borderRadius: radius.md, backgroundColor: sel ? colors.primarySoft : pressed ? colors.hover : 'transparent' })}
              >
                <View style={{ flex: 1 }}>
                  <Text color={sel ? colors.primary : colors.text} weight={sel ? 'semibold' : 'regular'}>
                    {item.label}
                  </Text>
                  {!!item.description && <Text variant="small">{item.description}</Text>}
                </View>
                {sel && <Icon name="check" size={16} color={colors.primary} />}
              </Pressable>
            );
          }}
          ListEmptyComponent={<Text variant="muted" style={{ padding: 12 }}>No matches</Text>}
        />
      </Modal>
    </>
  );
  return (
    <Field label={label} required={required} error={error} hint={hint} style={containerStyle}>
      {control}
    </Field>
  );
}

export function Switch({ label, value, onChange, disabled, description }: { label: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean; description?: string }) {
  const { colors } = useTheme();
  return (
    <Pressable accessibilityRole="switch" accessibilityState={{ checked: value, disabled }} disabled={disabled} onPress={() => onChange(!value)} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
      <View style={{ flex: 1 }}>
        <Text variant="label">{label}</Text>
        {!!description && <Text variant="small">{description}</Text>}
      </View>
      <RNSwitch value={value} onValueChange={onChange} disabled={disabled} trackColor={{ true: colors.primary, false: colors.borderStrong }} thumbColor="#fff" />
    </Pressable>
  );
}

export function Checkbox({ label, value, onChange, disabled }: { label: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const { colors } = useTheme();
  return (
    <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: value, disabled }} disabled={disabled} onPress={() => onChange(!value)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, opacity: disabled ? 0.6 : 1 }}>
      <View style={{ width: 18, height: 18, borderRadius: 4, borderWidth: 1.5, borderColor: value ? colors.primary : colors.borderStrong, backgroundColor: value ? colors.primary : colors.surface, alignItems: 'center', justifyContent: 'center' }}>
        {value && <Icon name="check" size={12} color={colors.onPrimary} strokeWidth={3} />}
      </View>
      <Text>{label}</Text>
    </Pressable>
  );
}

/** Radio group (vertical or horizontal). */
export function RadioGroup<V extends string>({ options, value, onChange, disabled, horizontal }: { options: Option<V>[]; value: V; onChange: (v: V) => void; disabled?: boolean; horizontal?: boolean }) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: horizontal ? 'row' : 'column', gap: horizontal ? 18 : 10, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const sel = o.value === value;
        return (
          <Pressable key={o.value} accessibilityRole="radio" accessibilityState={{ checked: sel, disabled }} disabled={disabled} onPress={() => onChange(o.value)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, opacity: disabled ? 0.6 : 1 }}>
            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 1.5, borderColor: sel ? colors.primary : colors.borderStrong, alignItems: 'center', justifyContent: 'center' }}>
              {sel && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary }} />}
            </View>
            <View>
              <Text>{o.label}</Text>
              {!!o.description && <Text variant="small">{o.description}</Text>}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}
